import { useCallback, useEffect, useRef, useState } from 'react';
import { site } from '@/config/site';
import type { ScreenSaverId } from '@/config/screensavers';
import { useSettingsStore } from '@/settings/store';
import type { Saver } from './savers';
import css from './ScreenSaver.module.css';

/**
 * Events that count as "somebody is there". `pointermove` is deliberately
 * included even though it fires constantly — that is the point, an idle
 * machine is one where the mouse has not moved.
 */
const ACTIVITY = [
  'pointerdown',
  'pointermove',
  'wheel',
  'keydown',
  'touchstart',
  'focus',
] as const;

/** How often the idle clock is checked. A minute's resolution needs no better. */
const CHECK_MS = 5_000;

/**
 * The screen saver.
 *
 * Two jobs, kept apart: deciding when to run (an idle clock over the whole
 * document) and running (a canvas and a `requestAnimationFrame` loop). The
 * canvas is only mounted while the saver is up, so an idle desktop costs one
 * interval and nothing else.
 */
export function ScreenSaverLayer() {
  const saverId = useSettingsStore((s) => s.screenSaverId);
  const waitMinutes = useSettingsStore((s) => s.screenSaverWait);
  const previewToken = useSettingsStore((s) => s.screenSaverPreview);

  const [running, setRunning] = useState(false);
  const lastActivity = useRef(performance.now());

  const stop = useCallback(() => setRunning(false), []);

  /* ------------------------------------------------------------ the clock */

  useEffect(() => {
    const touch = () => {
      lastActivity.current = performance.now();
    };
    for (const type of ACTIVITY) window.addEventListener(type, touch, { passive: true });
    return () => {
      for (const type of ACTIVITY) window.removeEventListener(type, touch);
    };
  }, []);

  useEffect(() => {
    if (saverId === 'none') return;
    const waitMs = Math.max(1, waitMinutes) * 60_000;

    const id = window.setInterval(() => {
      // A hidden tab is not an idle desktop — nobody is looking at it, and
      // waking to a screen saver on return would be a strange greeting.
      if (document.hidden) {
        lastActivity.current = performance.now();
        return;
      }
      if (performance.now() - lastActivity.current >= waitMs) setRunning(true);
    }, CHECK_MS);

    return () => window.clearInterval(id);
  }, [saverId, waitMinutes]);

  // Display Properties' Preview button bumps a counter rather than calling in
  // here, so the settings store stays the only thing the two share.
  const firstToken = useRef(previewToken);
  useEffect(() => {
    if (previewToken === firstToken.current) return;
    firstToken.current = previewToken;
    if (saverId !== 'none') setRunning(true);
  }, [previewToken, saverId]);

  /* ------------------------------------------------------------- dismissal */

  useEffect(() => {
    if (!running) return;

    const wake = () => {
      lastActivity.current = performance.now();
      stop();
    };
    // Capture, so a stray click cannot reach the desktop underneath: the first
    // gesture wakes the machine and does nothing else, as it always did.
    const swallow = (e: Event) => {
      e.preventDefault();
      e.stopPropagation();
      wake();
    };

    window.addEventListener('pointerdown', swallow, true);
    window.addEventListener('keydown', swallow, true);
    window.addEventListener('wheel', wake, { capture: true, passive: true });
    // A mouse that is already moving must not dismiss it instantly, so movement
    // only counts after the pointer has actually travelled.
    let startedAt: { x: number; y: number } | null = null;
    const onMove = (e: PointerEvent) => {
      if (!startedAt) {
        startedAt = { x: e.clientX, y: e.clientY };
        return;
      }
      if (Math.hypot(e.clientX - startedAt.x, e.clientY - startedAt.y) > 12) wake();
    };
    window.addEventListener('pointermove', onMove, true);

    return () => {
      window.removeEventListener('pointerdown', swallow, true);
      window.removeEventListener('keydown', swallow, true);
      window.removeEventListener('wheel', wake, true);
      window.removeEventListener('pointermove', onMove, true);
    };
  }, [running, stop]);

  if (!running || saverId === 'none') return null;
  return <SaverCanvas id={saverId} />;
}

interface SaverCanvasProps {
  id: Exclude<ScreenSaverId, 'none'>;
}

function SaverCanvas({ id }: SaverCanvasProps) {
  const canvasRef = useRef<HTMLCanvasElement>(null);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    // One backing pixel per CSS pixel: a full-screen canvas at 2x costs four
    // times the fill for an effect made of blurred glows and 2px stars.
    let saver: Saver | null = null;
    const size = () => {
      const w = Math.max(1, window.innerWidth);
      const h = Math.max(1, window.innerHeight);
      canvas.width = w;
      canvas.height = h;
      if (saver) saver.resize(w, h);
      return { w, h };
    };

    const onResize = () => size();
    window.addEventListener('resize', onResize);

    let frame = 0;
    let cancelled = false;

    // The savers are a few kB of drawing code that most visitors will never
    // reach, so they are fetched when one actually runs rather than shipped
    // with the desktop. Ten idle minutes is plenty of time for one request.
    void import('./savers').then(({ createSaver }) => {
      if (cancelled) return;
      const { w, h } = size();
      saver = createSaver(id, w, h, { text: `${site.title} — ${site.tagline}` });
      if (!saver) return;

      const tick = (ms: number) => {
        saver?.draw(ctx, ms);
        frame = window.requestAnimationFrame(tick);
      };
      frame = window.requestAnimationFrame(tick);
    });

    return () => {
      cancelled = true;
      window.cancelAnimationFrame(frame);
      window.removeEventListener('resize', onResize);
    };
  }, [id]);

  return (
    <div className={css.layer} role="presentation">
      <canvas ref={canvasRef} className={css.canvas} />
    </div>
  );
}
